const TEST_EMAIL_DOMAIN = '@inbox.testmail.app'
const DISPLAY_DOMAIN = '@gmail.com'

/**
 * Cosmetic, display-only cleanup of bot-generated test emails.
 *
 * A testmail address of the form
 *   `{workspaceId}.{first}.{last}.{identityId}@inbox.testmail.app`
 * becomes `{first}.{last}.{shortId}@gmail.com`, where `shortId` is the last 4
 * characters of the identity id before the trailing `local` build suffix — e.g.
 *   `33e34.sophia.gagnon.1789387960048local@inbox.testmail.app`
 *   -> `sophia.gagnon.0048@gmail.com`
 *
 * Every non-testmail email is returned unchanged. Never persist the result and
 * never use it for mailto/search/keys — keep the real address for those.
 */
export function formatDisplayEmail(email?: string | null): string {
  const value = (email ?? '').trim()
  if (!value.toLowerCase().endsWith(TEST_EMAIL_DOMAIN)) return email ?? ''

  const parts = value
    .slice(0, -TEST_EMAIL_DOMAIN.length)
    .split('.')
    .filter(Boolean)
  if (parts.length <= 1) return `${shortIdentityId(parts[0] ?? 'user')}${DISPLAY_DOMAIN}`

  const rest = parts.slice(1) // drop the leading workspace/namespace id
  rest[rest.length - 1] = shortIdentityId(rest[rest.length - 1]) // shorten the identity id
  return `${rest.join('.')}${DISPLAY_DOMAIN}`
}

/** Last 4 characters of the identity id, ignoring a trailing `local` build suffix. */
function shortIdentityId(id: string): string {
  return id.replace(/local$/i, '').slice(-4) || id.slice(-4)
}
